<template>
	<v-row>
		<v-col>

			<ui-box new-forms>

				<form-title title="All Prizes">
					Create new prizes or edit existing ones. Add prizes for redemption by
					passholders.
					<template v-slot:button>
						<div class="align-center d-flex">
							<v-text-field
									v-model="prizeSearchTerm"
									class="rounded-lg"
									dense
									hide-details
									label="Search Prizes"
									outlined
							></v-text-field>
							<v-btn
									class="ms-4 rounded-lg"
									color="secondary"
									depressed
									:disabled="isSaving"
									:href="addNewPrizeUrl"
									outlined
							>
								<v-icon>
									mdi-plus
								</v-icon>
								Add New
							</v-btn>
						</div>
					</template>
				</form-title>

				<form-divider inner/>

				<v-row>
					<v-col>
						<div v-if="displayAllPrizes.length" class="side-scroll">

							<template v-for="(prize, prizeIdx) in prizeSearchResults">
								<v-card class="pb-16 scrolling-card">
									<v-img
											:aspect-ratio="2"
											class="white--text align-end"
											:src="prize.media[0]?.url || '/images/placehold.co.png'"
									>
									</v-img>

									<v-card-text>
										<div class="font-weight-bold line-clamp-1 text-body-2">
											{{ prize.name }}
										</div>
										<form-divider inner/>
										<div
												class="line-clamp-4 text-caption"
												:inner-html.prop="prize.highlights | nl2br"
										></div>
									</v-card-text>

									<v-card-actions class="position-absolute w-100" style="bottom: 0;">
										<div class="w-100">
											<form-divider inner/>
											<div class="d-flex justify-end w-100">
												<v-btn
														class="me-1"
														color="secondary"
														depressed
														fab
														:href="editPrizeUrl(prize)"
														outlined
														small
												>
													<v-icon>
														mdi-pencil-outline
													</v-icon>
												</v-btn>
												<v-btn
														color="secondary"
														depressed
														fab
														:href="duplicatePrizeUrl(prize)"
														outlined
														small
												>
													<v-icon>
														mdi-content-copy
													</v-icon>
												</v-btn>
												<v-divider
														class="mx-2 my-1"
														vertical
												></v-divider>
												<v-btn
														color="success"
														depressed
														:disabled="localPass.prizes.some(p => p.id === prize.id)"
														fab
														:outlined="!localPass.prizes.some(p => p.id === prize.id)"
														small
														@click="addPrize(prize)"
												>
													<v-icon>
														mdi-plus
													</v-icon>
												</v-btn>
											</div>
										</div>
									</v-card-actions>
								</v-card>
							</template>

						</div>
					</v-col>
				</v-row>
			</ui-box>

			<form-divider/>

			<v-form v-model="formIsValid.prizes" ref="prizesForm">

				<ui-box :eager="!!pass.prizes.length" :form="$refs.prizesForm" new-forms>

					<form-title title="Added Prizes">
						Manage how passholders will be able to earn prizes. The order of prizes here
						will be reflected on the mobile pass.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<ui-box no-padding>

								<template v-if="selectedPrizes.length === 0">

									<div
											class="align-center d-flex justify-center w-100"
											style="height: 80px;"
									>
										Oops, no prizes to give away yet!
									</div>

								</template>

								<template v-else>

									<v-container
											v-if="$vuetify.breakpoint.mdAndUp"
											style="line-height: 1rem;"
									>
										<v-row no-gutters
													 class="font-weight-bold grey--text mb-0 text-caption text--darken-2"
										>
											<v-col cols="auto" style="min-width: 30px;"></v-col>
											<v-col class="flex-grow-1">
												<v-row>
													<v-col class="flex-grow-1">Name</v-col>
													<v-col cols="3">Redemption Cost</v-col>
													<v-col cols="3">Redemption Limit</v-col>
												</v-row>
											</v-col>
											<v-col
													class="d-flex justify-end"
													cols="auto"
													style="min-width: 50px;"
											>
												Action
											</v-col>
										</v-row>
									</v-container>

									<v-divider
											v-if="$vuetify.breakpoint.mdAndUp"
											class="opacity-80"
									></v-divider>

									<v-container style="line-height: 1rem;">
										<draggable
												v-model="selectedPrizes"
												class="my-n4 sortables"
												direction="vertical"
												filter="input"
												tag="div"
												@start="onDragStart"
												@end="onDragEnd"
										>
											<transition-group type="transition"
																				:name="!drag ? 'flip-list' : null">
												<v-row
														v-for="(prize, prizeIdx) in selectedPrizes"
														:key="prize.id"
														class="ma-0"
														no-gutters
												>
													<v-col cols="auto" style="min-width: 30px;">
														<v-icon
																:style="{
																	marginTop: $vuetify.breakpoint.smAndDown ? 7 : 16
																}"
														>
															mdi-drag-vertical
														</v-icon>
													</v-col>
													<v-col class="flex-grow-1">
														<v-row>
															<v-col
																	:cols="$vuetify.breakpoint.smAndDown ? 12 : null"
																	class="flex-grow-1"
															>
																<v-text-field
																		v-model="prize.name"
																		class="rounded-lg mb-2"
																		:dense="$vuetify.breakpoint.smAndDown"
																		hide-details="auto"
																		label="Prize Name"
																		outlined
																		persistent-hint
																		type="text"
																		readonly
																></v-text-field>
																<v-chip
																		:color="prize.active ? 'primary' : 'warning lighten-1'"
																		class="float-left rounded-pill mt-n4 ml-n4 position-absolute"
																		label
																		light
																		small
																>
																<span class="white--text">
																	{{ prize.active ? 'Active' : 'Inactive' }}
																</span>
																</v-chip>
															</v-col>
															<v-col cols="5" md="3">
																<v-text-field
																		v-model.number="prize.amount"
																		class="rounded-lg required"
																		:dense="$vuetify.breakpoint.smAndDown"
																		hide-details="auto"
																		hide-spin-buttons
																		label="Cost"
																		outlined
																		:rules="rules.prize_points"
																		suffix="pts"
																		type="number"
																		@wheel.native.prevent
																></v-text-field>
															</v-col>
															<v-col cols="7" md="3">
																<v-text-field
																		v-model.number="prize.max_purchases"
																		class="rounded-lg"
																		:dense="$vuetify.breakpoint.smAndDown"
																		hide-details="auto"
																		hide-spin-buttons
																		hint="Leave blank for unlimited"
																		label="Redemption Limit"
																		outlined
																		:rules="rules.prize_max_purchases"
																		type="number"
																		@wheel.native.prevent
																></v-text-field>
															</v-col>
														</v-row>
													</v-col>
													<v-col
															class="d-flex justify-end"
															cols="auto"
															style="min-width: 50px;"
													>
														<v-btn
																color="error"
																depressed
																fab
																outlined
																small
																:style="{
																	marginTop: $vuetify.breakpoint.smAndDown ? 5 : 14
																}"
																@click="removePrizeFromSelectedPrizes(prizeIdx)"
														>
															<v-icon>
																mdi-trash-can-outline
															</v-icon>
														</v-btn>
													</v-col>
												</v-row>
											</transition-group>
										</draggable>
									</v-container>

								</template>
							</ui-box>

						</v-col>
					</v-row>

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.defaultPoints" ref="defaultPointsForm" class="mb-8">

				<ui-box :eager="!!pass.prizes.length" :form="$refs.defaultPointsForm" new-forms>

					<form-title title="Default Points">
						The following rule settings apply to all check-ins and referrals in your pass.
					</form-title>

					<form-divider inner/>

					<form-subsection title="New Pass Holder Signup Bonus">
						Set the default points a new pass holder receives when they first sign up.
					</form-subsection>

					<v-row>
						<v-col cols="12" md="12">
							<v-row>
								<v-col cols="6">
									<v-text-field
										v-model.number="signupPoints"
										class="rounded-lg"
										hide-details="auto"
										label="New Pass Holder Signup Points"
										min="0"
										outlined
										:rules="rules.signup_points"
										type="number"
										@wheel.native.prevent
									></v-text-field>
								</v-col>
							</v-row>
						</v-col>
					</v-row>

					<form-subsection title="Check-In Points">
						Set the default points a pass-holder receives by successfully checking into a
						Place.
					</form-subsection>

					<v-row>
						<v-col cols="12" md="12">
							<v-row>
								<v-col cols="6">
									<v-text-field
											v-model.number="localPass.rules[awardPointsOnRedemptionIdx].settings.default_checkin_points"
											class="rounded-lg"
											hide-details="auto"
											label="Check-In Points"
											min="0"
											outlined
											:rules="rules.redemption_points"
											type="number"
											@wheel.native.prevent
									></v-text-field>
								</v-col>
							</v-row>
						</v-col>
					</v-row>

					<form-subsection title="Referral Points">
						If you allow Social Sharing for this pass, users who share the pass and
						successfully refer someone to sign up for a pass can receive points. Set the
						amount of points the user will receive for a successful referral.
					</form-subsection>

					<v-row>
						<v-col cols="12" md="12">
							<v-row>
								<v-col cols="6">
									<v-text-field
											v-model.number="referralPoints"
											class="rounded-lg"
											hide-details="auto"
											label="Referral Points"
											min="0"
											outlined
											:rules="rules.referral_points"
											type="number"
											@wheel.native.prevent
											@blur="onReferralPointsBlur"
									></v-text-field>
								</v-col>
							</v-row>
						</v-col>

					</v-row>

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.expirePoints" ref="expirePointsForm" class="mb-8">

				<ui-box :eager="!!pass.prizes.length" :form="$refs.expirePointsForm" new-forms>

					<form-title title="Point Expiration for Prize Redemption">
						The following rule settings dictate how long a passholder has to use their
						points for prize redemption after their pass expires.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" md="12">
							<v-row>
								<v-col>
									<v-select
											v-model="endType"
											class="rounded-lg"
											hide-details="auto"
											:items="[
												{name: 'Days after end', value: 'duration'}, 
												{name: 'Upon Pass Expiration', value: 'end'}, 
												{name: 'Specific Date', value: 'date'}]"
											item-text="name"
											label="Points redemption end date"
											outlined
									></v-select>
								</v-col>
								<v-col>
									<template v-if="endType === 'duration'">
										<v-text-field
												v-model.number="localPass.rules[awardPointsOnRedemptionIdx].settings.usable_until_days"
												class="rounded-lg required"
												hide-details="auto"
												hide-spin-buttons
												label="Days"
												min="0"
												outlined
												:rules="rules.usable_until_days"
												suffix="after pass end date"
												type="number"
												@wheel.native.prevent
										></v-text-field>
									</template>

									<template v-if="endType === 'date'">
										<bw-date-time-picker
												v-model="localPass.rules[awardPointsOnRedemptionIdx].settings.end_date_time"
												hide-details="auto"
												label="Points Valid Until"
												:required="true"
												:rules="rules.usable_until_date"
										></bw-date-time-picker>
									</template>
								</v-col>
							</v-row>
						</v-col>

					</v-row>

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.customPoints" ref="customPointsForm" class="mb-8">

				<ui-box :eager="!!pass.prizes.length" :form="$refs.customPointsForm" new-forms>

					<form-title title="Custom Points Settings">
						You may decide to award custom points for certain things within your pass for
						various reasons. Leave blank to use the default values above.
					</form-title>

					<form-divider inner/>

					<form-subsection title="Search Filters"/>

					<v-row>
						<v-col>
							<v-select
									v-model="filterCategories"
									class="rounded-lg"
									clearable
									deletable-chips
									hide-details="auto"
									:items="allCategories"
									item-text="name"
									item-value="category_id"
									label="Filter by Venue Category"
									multiple
									outlined
									small-chips
							></v-select>
						</v-col>
						<v-col>
							<v-select
									v-model="filterProductTypes"
									class="rounded-lg"
									clearable
									deletable-chips
									hide-details="auto"
									:items="allProductTypes"
									item-text="name"
									label="Filter by Product Type"
									multiple
									outlined
									small-chips
							></v-select>
						</v-col>
						<v-col>
							<bw-search-select
									v-model="filterRedemptionValidation"
									:initial-items="[{name: 'None', value: ''}, {name: 'Pin', value: 'pin'}, {name: 'GPS', value: 'gps'}, {name: 'GPS or Pin', value: 'gpsOrPin'}, {name: 'QR Code Scan', value: 'qr'}]"
									item-text="name"
									item-value="value"
									label="Filter by Redemption Validation"
									small-chips
							></bw-search-select>
						</v-col>
					</v-row>

					<form-divider inner/>

					<template v-if="!this.passProductsWithPointsFiltered.length">

						<ui-box no-padding>
							<div
									class="align-center d-flex justify-center w-100"
									style="height: 80px;"
							>
								Oops, no results!
							</div>
						</ui-box>

					</template>

					<template v-else>
						<v-row
								v-for="(productWithPoints, index) in this.passProductsWithPointsFiltered"
								:key="productWithPoints.product.id"
						>
							<v-tooltip color="info" nudge-top="40" open-delay="250" bottom>
								<template v-slot:activator="{ on }">
									<v-col v-on="on" cols="8" sm="6">
										<v-text-field
												v-model="productWithPoints.product.name"
												class="rounded-lg"
												disabled
												hide-details="auto"
												:label="productWithPoints.product.venueName"
												outlined
												type="text"
												readonly
										></v-text-field>
									</v-col>
								</template>
								<span>{{ getPlaceAddress(productWithPoints.product.venue_id) }}</span>
							</v-tooltip>
							<v-col cols="4" sm="3">
								<v-text-field
										v-model.number="productWithPoints.points"
										class="rounded-lg"
										hide-details="auto"
										label="Customer Earns"
										outlined
										suffix="pts"
										type="number"
										@change="updateProductPoints(productWithPoints)"
										@wheel.native.prevent
								></v-text-field>
							</v-col>
						</v-row>
					</template>

				</ui-box>
			</v-form>

			<bw-modal v-if="showSocialSharingAlert" title="Social Sharing Enablement"
								:width="800">
				<template #body>
					<v-row>
						<v-col>
							<span class="text-body-2">
								<span class="font-weight-bold">Warning: </span>
								In order for referral points to work, you need to enable 
								Social Sharing on the channels that this pass is in.
							</span>
						</v-col>
					</v-row>
				</template>
				<template #footer>
					<div class="align-center d-flex justify-space-between w-100">
						<span></span>
						<div class="d-flex justify-end">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									text
									@click="cancelSocialSharingAlert"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:disabled="isSaving"
									:large="$vuetify.breakpoint.mdAndUp"
									@click="saveSocialSharingAlert"
							>
								Enable
							</v-btn>
						</div>
					</div>
				</template>
			</bw-modal>

			<save-form
					:refs="$refs"
					:is-saving="isSaving"
					new-forms
					save-text="Save"
					@cancel-action="exit"
					@save-action="save"
			></save-form>

		</v-col>
	</v-row>
</template>

<script>
import draggable from 'vuedraggable';
import {passHelpers} from "../../helpers";
import FormSubsection from "../../../UI/Form/FormSubsection.vue";

export default {
	name: "Gamification",
	components: {
		FormSubsection,
		draggable
	},
	props: {
		extraData: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object,
			default: () => ({})
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		places: {
			type: Array,
			default: () => []
		},
		products: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			allPrizesSelection: [],
			allPrizes: [],
			checkin: {
				...cloneDeep(this.pass),
				...{
					highlights: this.pass.highlights || passHelpers.getPassHighlights(),
					redemption_policy: passHelpers.getRedemptionPolicyObject(this.pass),
					media: this.pass.media || [],
					use_points: true,
					rules: passHelpers.getAwardPoinsOnRedemptionRule(this.pass, this.extraData),
				}
			},
			drag: false,
			filterCategories: [],
			filterProductTypes: [],
			filterRedemptionValidation: [],
			formIsValid: {
				customPoints: false,
				defaultPoints: false,
				expirePoints: false,
				prizes: false
			},
			isSaving: false,
			localPass: {
				...this.pass,
				prizes: this.pass?.prizes || []
			},
			localProducts: this.products,
			passProductsWithPoints: [],
			prizesHeaders: [
				{
					align: 'start',
					mandatory: true,
					text: 'Prize',
					value: 'name',
					width: '90%'
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Actions',
					value: 'actions',
					width: '10%'
				}
			],
			prizeSearchTerm: '',
			prizesTableKey: 0,
			rules: {
				name: [v => !!v || 'Name is required.'],
				prize_points: [
					v => !!v || 'Prize points are required',
					v => (v && v > 0) || 'Prize points must be greater than 0.',
				],
				prize_max_purchases: [
					v => !v || (v > 0) || 'Max purchases must be greater than 0.s'
				],
				redemption_points: [
					v => !v || (v >= 0) || 'Points must be greater than 0.',
				],
				usable_until_days: [
					v => {
						if(this.endType === 'duration') {
							return (v && v >= 0) || 'Days must be 0 or greater.';
						}
						else {
							return true;
						}
					}
				],
				usable_until_date: [
					v => {
						if(this.endType !== 'duration') {
							return !!v || 'A date is required.';
						}
						else {
							return true;
						}
					}
				],
			},
			showSocialSharingAlert: false,
		};
	},
	mounted() {
		this.setupPassProductsWithPoints();
		this.refreshAllPrizes();
	},
	computed: {

		addNewPrizeUrl() {
			const { id: passId } = this.pass;
			const { id: partnerId } = this.partner;
			return `/partner-portal/${partnerId}/prizes?from_pass=${passId}`;
		},

		allCategories() {
			return Object.values(this.extraData.venueCategories);
		},

		allProductsById() {
			return this.keyById(this.extraData.passProducts);
		},

		allProductTypes() {
			let types = {};
			this.extraData.passProducts.forEach(product => {
				const productTypeCode = product?.product_type_code;
				if(productTypeCode) {
					types[productTypeCode] = {
						'value': productTypeCode,
						'name': productTypeCode,
					};
				}
			});
			types = Object.values(types);
			types.push({
				'value': null,
				'name': 'Not Set',
			});
			return types;
		},

		awardPointsOnRedemptionIdx() {
			return this.ruleIdx('awardPointsOnRedemption');
		},

		awardPointsOnSignUpIdx() {
			return this.ruleIdx('awardPointsOnSignUp');
		},

		displayAllPrizes() {
			return this.allPrizes;
		},

		editingPrizeHero: {
			get() {
				if(!this.editingPrize) return;
				let m = this.editingPrize.media.find(x => x.location === 'Hero');
				if(!m) {
					return {
						location: 'Hero',
						name: 'Hero',
						cropperSettings: {
							limitAR: '2:1',
							x: 0,
							y: 0
						}
					};
				}
				return {
					...m,
					minDimensions: false,
					cropperSettings: m.cropperSettings || {
						limitAR: '2:1',
						x: 0,
						y: 0
					}
				};
			},
			set(v) {
				if(!this.editingPrize) return;
				const index = this.editingPrize.media.findIndex(x => x.location === 'Hero');
				if(index !== -1) {
					this.$set(this.editingPrize.media, index, {
						...this.editingPrize.media[index],
						...v,
						minDimensions: false,
						cropperSettings: {
							limitAR: '2:1',
							x: 0,
							y: 0
						}
					});
				}
			}
		},

		endType: {
			get() {
				return this.localPass?.rules?.[this.awardPointsOnRedemptionIdx]?.settings?.end_type ?? 'end';
			},
			set(value) {
				if(value === 'duration') {
					this.$set(this.localPass.rules[this.awardPointsOnRedemptionIdx].settings, 'end_date_time', null);
				}
				else if(value === 'end') {
					this.$set(this.localPass.rules[this.awardPointsOnRedemptionIdx].settings, 'end_date_time', null);
					this.$set(this.localPass.rules[this.awardPointsOnRedemptionIdx].settings, 'usable_until_days', null);
				}
				else {
					this.$set(this.localPass.rules[this.awardPointsOnRedemptionIdx].settings, 'usable_until_days', null);
				}
				this.$set(this.localPass.rules[this.awardPointsOnRedemptionIdx].settings, 'end_type', value);
			}
		},

		passProductsWithPointsFiltered() {
			return this.passProductsWithPoints.filter(this.productMatches).sort((a, b) => {
				if(a.product.r_venue.name < b.product.r_venue.name) return -1;
				if(a.product.r_venue.name > b.product.r_venue.name) return 1;
				if(a.product.name < b.product.name) return -1;
				if(a.product.name > b.product.name) return 1;
				return 0;
			});
		},

		prizeSearchResults() {
			if(!this.prizeSearchTerm) return this.displayAllPrizes;

			return this.displayAllPrizes.filter(prize => prize.name.toLowerCase().includes(this.prizeSearchTerm.toLowerCase()));
		},

		referralPoints: {
			get() {
				return this.localPass?.rules?.[this.awardPointsOnSignUpIdx]?.settings?.referral_points ?? 0;
			},
			set(value) {
				const idx = this.awardPointsOnSignUpIdx;
				if(idx < 0) {
					this.localPass.rules.push({
						rule_id: this.extraData.ruleAwardPointsOnSign,
						file: 'awardPointsOnSignUp',
						settings: {}
					});
				}
				this.$set(this.localPass.rules[this.awardPointsOnSignUpIdx].settings, 'referral_points', value);
			}
		},


		selectedPrizes: {
			get() {
				return this.localPass.prizes;
			},
			set(value) {
				this.localPass.prizes = value;
			}
		},

		signupPoints: {
			get() {
				return this.localPass?.rules?.[this.awardPointsOnSignUpIdx]?.settings?.signup_points ?? 0;
			},
			set(value) {
				const idx = this.awardPointsOnSignUpIdx;
				if(idx < 0) {
					this.localPass.rules.push({
						rule_id: this.extraData.ruleAwardPointsOnSign,
						file: 'awardPointsOnSignUp',
						settings: {}
					});
				}
				this.$set(this.localPass.rules[this.awardPointsOnSignUpIdx].settings, 'signup_points', value);
			}
		},
	},
	methods: {
		addPrize(prize) {
			let maxSortOrder = 0;
			this.localPass.prizes.forEach(prize => {
				if(prize.sort_order > maxSortOrder) {
					maxSortOrder = prize.sort_order;
				}
			});
			this.allPrizes.find((item) => {
				if(item.id === prize.id) {
					item.product_id = prize.id;
					item.amount = null;
					item.max_purchases = null;
					item.sort_order = ++maxSortOrder;
					this.localPass.prizes.push(item);
					return true;
				}
			});
		},

		addPrizeToSelectedPrizes() {
			//compute the max sort_order for all the this.localPass.prizes
			let maxSortOrder = 0;
			this.localPass.prizes.forEach(prize => {
				if(prize.sort_order > maxSortOrder) {
					maxSortOrder = prize.sort_order;
				}
			});
			this.allPrizesSelection.forEach(prizeId => {
				this.allPrizes.find((item) => {
					if(item.id === prizeId) {
						item.product_id = prizeId;
						item.amount = null;
						item.max_purchases = null;
						item.sort_order = ++maxSortOrder;
						this.localPass.prizes.push(item);
						return true;
					}
				});
			});
			this.allPrizesSelection = [];
		},


		cancelSocialSharingAlert() {
			this.showSocialSharingAlert = false;
		},

		duplicatePrizeUrl({ id: prizeId }) {
			const { id: passId } = this.pass;
			const { id: partnerId } = this.partner;
			return `/partner-portal/${partnerId}/prizes?from_pass=${passId}&prize_id=${prizeId}&clone=1`;
		},

		editPrizeUrl({ id: prizeId }) {
			const { id: passId } = this.pass;
			const { id: partnerId } = this.partner;
			return `/partner-portal/${partnerId}/prizes?from_pass=${passId}&prize_id=${prizeId}`;
		},

		exit() {
			window.location.href = `/partner-portal/${this.partner.id}/passes`;
		},

		getPlaceAddress(placeId) {
			return this.places.find(({ id }) => id === placeId)?.address;
		},

		ignoreDrag(el, e) {
			return e.target.tagName === 'INPUT';
		},

		keyById(items) {
			const result = {};
			items.forEach(item => result[item.id] = item);
			return result;
		},

		onDragEnd() {
			this.drag = false;
			this.sortSelectedPrizes();
		},

		onDragStart(event) {
			this.drag = true;
		},

		onReferralPointsBlur(event) {
			if(!this.extraData.socialSharingEnabled) this.showSocialSharingAlert = true;
		},

		productMatches(productWithPoints) {
			const plainProduct = this.allProductsById[productWithPoints.product.id];
			return this.productMatchesCategories(plainProduct) && this.productMatchesTypes(plainProduct) && this.productMatchesRedemptionValidation(plainProduct);
		},

		productMatchesCategories(product) {
			return this.filterCategories.length === 0 ||
					product.r_venue?.r_categories?.some(category =>
							this.filterCategories.some(filterCat => category.category_id === filterCat)
					);
		},

		productMatchesRedemptionValidation(product) {
			return this.filterRedemptionValidation.length === 0
					|| this.filterRedemptionValidation.some(filterRV => product.redemption_validation === filterRV);
		},

		productMatchesTypes(product) {
			return this.filterProductTypes.length === 0
					|| this.filterProductTypes.some(filterType => product.product_type_code === filterType);
		},

		async refreshAllPrizes() {
			try {
				const response = await axios.get(`/partner-portal/${this.partner.id}/prizes/list?active=true&deleted=false`);
				this.allPrizes = response.data.allPrizes;
				this.prizesTableKey += 1;
				this.setupAllPrizesTickets();
			}
			catch (error) {
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			}
		},

		removePrizeFromSelectedPrizes(index) {
			this.localPass.prizes.splice(index, 1);
			this.sortSelectedPrizes();
		},

		ruleIdx(fileName) {
			return this.localPass.rules.findIndex(rule => rule.file === fileName)
		},

		saveSocialSharingAlert() {
			this.showSocialSharingAlert = false;
			this.isSaving = true;
			const url = `/partner-portal/${this.partner.id}/passes/enable-sharing/${this.localPass.id || ''}`;

			axios.put(url, this.localPass)
					.then(({ data }) => {
						this.extraData.socialSharingEnabled = true;
						this.isSaving = false;
					})
					.catch(error => {
						this.isSaving = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},

		setupAllPrizesTickets() {
			if(this.allPrizes?.length > 0) {
				this.allPrizes.forEach(prize => {
					if(prize.tickets?.length > 0) {
						prize.tickets.forEach(ticket => {
							//Find if exists the attribute with attribute_id = 0 for the prize
							if(ticket.attribute_id === 0) {
								prize.ticketQuantity = ticket.quantity;
								prize.ticketId = ticket.id;
							}
							else {
								//Find the attribute in the prize attributes
								const attribute = prize.attributes.find(attribute => attribute.id === ticket.attribute_id);
								if(attribute) {
									attribute.ticketQuantity = ticket.quantity;
									attribute.ticketId = ticket.id;
								}
							}
						});
					}
				});
			}
		},

		save() {
			this.isSaving = true;
			const url = `/partner-portal/${this.partner.id}/passes/save-pass/${this.localPass.id || ''}`;

			axios.put(url, this.localPass)
					.then(({ data }) => {
						const url = this.localPass.id
								? `/partner-portal/${this.partner.id}/passes/${this.localPass.id}/categories`
								: `/partner-portal/${this.partner.id}/passes/${data.pass.id}`
						window.location = url;
					})
					.catch(error => {
						this.isSaving = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},

		setupPassProductsWithPoints() {
			if(this.extraData.passProducts && this.extraData.passProducts.length) {
				this.extraData.passProducts.forEach(product => {
					let points = null;
					if(this.localPass.rules[this.awardPointsOnRedemptionIdx]?.settings?.dealProducts) {
						for (const productId in this.localPass.rules[this.awardPointsOnRedemptionIdx]?.settings.dealProducts) {
							if(parseInt(productId) === product.id) {
								points = this.localPass.rules[this.awardPointsOnRedemptionIdx]?.settings.dealProducts[productId];
								break;
							}
						}
					}
					const passProductWithPoints = new PassProductWithPoints(product, points);
					this.passProductsWithPoints.push(passProductWithPoints);
				});
			}
		},

		sortSelectedPrizes() {
			let sortOrder = 1;
			this.localPass.prizes.forEach(prize => {
				prize.sort_order = sortOrder++;
			});
		},

		updateProductPoints(productWithPoints) {
			if(!this.localPass.rules[this.awardPointsOnRedemptionIdx]?.settings?.dealProducts ||
				Array.isArray(this.localPass.rules[this.awardPointsOnRedemptionIdx]?.settings.dealProducts)) {
				this.$set(this.localPass.rules[this.awardPointsOnRedemptionIdx]?.settings, 'dealProducts', {});
			}
			if(productWithPoints.points !== null && productWithPoints.points !== '') {
				this.localPass.rules[this.awardPointsOnRedemptionIdx].settings.dealProducts[productWithPoints.product.id] = parseInt(productWithPoints.points);
			}
			else {
				delete this.localPass.rules[this.awardPointsOnRedemptionIdx].settings.dealProducts[productWithPoints.product.id];
			}
		},
	}
}

function PassProductWithPoints(product, points) {
	this.product = product;
	this.points = points;
}

</script>

<style lang="scss" scoped>
::v-deep #product-req-table td {
	vertical-align: top !important;
}

.side-scroll {
	display: flex;
	margin: 0rem;
	white-space: nowrap;
	overflow-x: scroll; /* Always show vertical scrollbar */

	/* For Webkit browsers */
	&::-webkit-scrollbar {
		background-color: #fcfcfc; /* Adjust color and opacity to your preference */
		border-radius: 0 0 8px 8px;
		box-shadow: 0 -1px 0 0 #e5e5e5;
		height: 16px; /* Adjust to your preference */
	}

	&::-webkit-scrollbar-track {
	}

	&::-webkit-scrollbar-thumb {
		background-color: #bbbbbb; /* Adjust color and opacity to your preference */
		border-radius: 8px; /* Adjust to your preference */
		border: solid 3px #fcfcfc;
		transition: background-color 0.3s linear;

		&:hover {
			background-color: #999999;
		}
	}
}

.scrolling-card {
	flex: 0 0 auto;
	min-width: 200px;
	width: 12dvw; /* or any fixed width */
	margin: 1rem;
}

.sortables > span > div {
	margin: 0 -1rem !important;
	padding: 1rem;

	&:hover {
		background-color: #f0f8f0 !important;
	}

	&:not(:first-child):not(.sortable-drag) {
		border-top: solid 1px rgba(0, 0, 0, 0.096)
	}

	& .v-input--selection-controls__input {
		margin-right: 0;
	}
}

.no-caret ::v-deep .v-input__append-inner {
	display: none;
}
</style>
